import React, { memo, useCallback } from 'react';
import { Badge, Button, Form, Table } from 'react-bootstrap';

import { MarathonMilestoneType } from '../../../../../../__gqltypes__';
import Chip from '../../../components/Chip';
import EditValue from '../../../components/EditValue';
import { getMilestoneTypeOption, groupMaxValue, MILESTONE_TYPE_OPTIONS, MMilestone } from '../utils';

function checkIfNumber(val: string): number | undefined {
  return Number.isNaN(+val) ? undefined : +val;
}

type RowProps = {
  milestone: MMilestone;
  index: number;
  error: string | null;
  // The group-max milestone is the group boundary: always COMBINED, auto-set, not editable.
  isGroupMax: boolean;
  onChangeValue: (index: number, value: number) => void;
  onChangeType: (index: number, type: MarathonMilestoneType) => void;
  onEditReward: (index: number, isPlus: boolean) => void;
  onRemoveRow: (index: number) => void;
};

const MilestoneRow = memo(function MilestoneRow({
  milestone,
  index,
  error,
  isGroupMax,
  onChangeValue,
  onChangeType,
  onEditReward,
  onRemoveRow,
}: RowProps): JSX.Element {
  const combinedOption = getMilestoneTypeOption(MarathonMilestoneType.COMBINED);
  // group-max is shown as COMBINED regardless of its stored type (auto-set on save)
  const typeOption = isGroupMax ? combinedOption : getMilestoneTypeOption(milestone.type);
  return (
    <tr>
      <td className="align-middle text-center">{index + 1}</td>
      <td className="align-middle">
        <div className="d-flex flex-column align-items-center">
          <EditValue
            name=""
            value={milestone.numberOfGuesses}
            handleValueChange={(val) => {
              onChangeValue(index, checkIfNumber(val) ?? 0);
            }}
          />
          {error && <Form.Text className="text-danger">{error}</Form.Text>}
        </div>
      </td>
      <td className="align-middle">
        <div className="d-flex justify-content-center align-items-center">
          <Badge className="mr-2" variant={typeOption.badgeVariant}>
            {typeOption.label}
          </Badge>
          {isGroupMax ? (
            <span
              className="text-muted small"
              title="Group boundary: the highest milestone of a group is always COMBINED (checkpoint + rest point). It is set automatically and cannot be changed."
            >
              <i className="fas fa-lock mr-1" />
              auto
            </span>
          ) : (
            <Form.Control
              as="select"
              size="sm"
              style={{ width: 130 }}
              value={milestone.type}
              onChange={(e) => onChangeType(index, e.target.value as MarathonMilestoneType)}
            >
              {MILESTONE_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Control>
          )}
        </div>
      </td>
      <td className="align-middle">
        <div className="d-flex justify-content-center align-items-center">
          <Chip reward={milestone.basicReward} canDelete={false} globalAction={() => onEditReward(index, false)} />
        </div>
      </td>
      <td className="align-middle">
        <div className="d-flex justify-content-center align-items-center">
          <Chip reward={milestone.plusReward} canDelete={false} globalAction={() => onEditReward(index, true)} />
        </div>
      </td>
      <td className="align-middle text-center">
        <Button size="sm" variant="outline-danger" title="Remove milestone" onClick={() => onRemoveRow(index)}>
          <i className="fas fa-trash" />
        </Button>
      </td>
    </tr>
  );
});

type Props = {
  milestones: MMilestone[];
  rowErrors: Array<string | null>;
  onChangeMilestone: (index: number, patch: Partial<MMilestone>) => void;
  onEditReward: (index: number, isPlus: boolean) => void;
  onRemoveRow: (index: number) => void;
};

export function MarathonMilestonesTable({
  milestones,
  rowErrors,
  onChangeMilestone,
  onEditReward,
  onRemoveRow,
}: Props): JSX.Element {
  const handleChangeValue = useCallback(
    (index: number, value: number) => {
      onChangeMilestone(index, { numberOfGuesses: value });
    },
    [onChangeMilestone]
  );

  const handleChangeType = useCallback(
    (index: number, type: MarathonMilestoneType) => {
      onChangeMilestone(index, { type });
    },
    [onChangeMilestone]
  );

  const maxValue = groupMaxValue(milestones);

  return (
    <Table bordered responsive hover size="sm">
      <thead className="text-center">
        <tr>
          <th style={{ width: 40 }}>#</th>
          <th>Number of guesses</th>
          <th>Type</th>
          <th>Basic reward</th>
          <th>Plus reward</th>
          <th style={{ width: 50 }} />
        </tr>
      </thead>
      <tbody>
        {milestones.length === 0 && (
          <tr>
            <td colSpan={6} className="text-center text-muted">
              No milestones yet.
            </td>
          </tr>
        )}
        {milestones.map((milestone, index) => (
          <MilestoneRow
            key={milestone._clientId}
            milestone={milestone}
            index={index}
            error={rowErrors[index] ?? null}
            isGroupMax={maxValue !== null && milestone.numberOfGuesses === maxValue}
            onChangeValue={handleChangeValue}
            onChangeType={handleChangeType}
            onEditReward={onEditReward}
            onRemoveRow={onRemoveRow}
          />
        ))}
      </tbody>
    </Table>
  );
}
